import { Component } from '@theme/component';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} recentlyViewedPanel
 */

/** How many products a tab shows before "Load more", and how many each press adds. */
const PAGE_SIZE = 4;

/** Mobile breakpoint - must track the `max-width: 749px` blocks in base.css. */
const MOBILE_QUERY = '(max-width: 749px)';

/**
 * @extends {Component<Refs>}
 */
class ProductRecommendationsTabs extends Component {
  /** @type {boolean} */
  #recentlyViewedLoaded = false;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {number | null} */
  #syncFrame = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupTabs();
    this.#setupScrollArrows();
    this.#setupPaging();
    // Prefetch Shopify related products so YMAL (or Nosto fallback) isn't blank on first open.
    this.#prefetchRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mobileQuery?.removeEventListener('change', this.#handleViewportChange);
    document.removeEventListener('product-recommendations-tabs:content', this.#requestSync);
    if (this.#syncFrame != null) cancelAnimationFrame(this.#syncFrame);
  }

  /** The scrollable row of whichever panel is showing. */
  #activeScroller() {
    const panel = this.refs.panels?.find((p) => !p.hidden) || this.refs.panels?.[0];
    return panel?.hasAttribute('data-nosto-native')
      ? panel.querySelector('.swiper-wrapper') || panel.querySelector('[data-recs-nosto]')
      : panel && this.#shownGrid(panel);
  }

  /**
   * The panel's `.resource-list--grid` that base.css is actually showing. Nosto's cards
   * grid sits before the fallback but is `display: none` until the panel is
   * `data-nosto-filled` (and not native) - and pdp-nosto-carousel.js can still fill it after
   * the fallback has been revealed, leaving it hidden for good. A bare first-grid lookup
   * then measured that hidden grid (0px wide), greying both arrows out (17tqamevfh1).
   * Decided from the panel's attributes rather than layout, so it also holds for a hidden tab.
   * @param {HTMLElement} panel
   * @returns {HTMLElement | undefined}
   */
  #shownGrid(panel) {
    const cardsShown = panel.hasAttribute('data-nosto-filled') && !panel.hasAttribute('data-nosto-native');
    return /** @type {HTMLElement[]} */ ([...panel.querySelectorAll('.resource-list--grid')]).find(
      (grid) => cardsShown || !grid.closest('[data-recs-nosto-cards]')
    );
  }

  #setupScrollArrows() {
    const buttons = [...this.querySelectorAll('[data-recs-scroll]')];

    // Dim an arrow when its direction has nowhere to go (end of the row, or nothing overflows).
    const syncArrows = () => {
      const scroller = this.#activeScroller();
      const max = scroller ? scroller.scrollWidth - scroller.clientWidth : 0;
      const left = scroller?.scrollLeft ?? 0;
      buttons.forEach((button) => {
        const remaining = button.getAttribute('data-recs-scroll') === 'prev' ? left : max - left;
        button.disabled = max <= 1 || remaining <= 1;
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const dir = button.getAttribute('data-recs-scroll') === 'prev' ? -1 : 1;
        const scroller = this.#activeScroller();
        if (!scroller) return;

        // Scroll by one item + gap. Falls back to the old fixed mobile step (150px item + 1px gap)
        // if the item width can't be measured (e.g. empty/loading panel).
        const item = scroller.querySelector(':scope > *');
        const step = item ? item.getBoundingClientRect().width + 1 : 151;
        scroller.scrollBy({ left: dir * step, behavior: 'smooth' });
      });
    });

    // scroll doesn't bubble, so capture it from any row; the rest re-measure after a tab swap,
    // a late fill (Nosto / recently viewed) or a resize.
    this.addEventListener('scroll', syncArrows, { capture: true, passive: true });
    this.addEventListener('click', () => requestAnimationFrame(syncArrows));
    this.addEventListener('product-recommendations:updated', syncArrows);
    document.addEventListener('product-recommendations-tabs:content', syncArrows);
    new ResizeObserver(syncArrows).observe(this);
    syncArrows();
  }

  /**
   * "Load more" paging. Every tab renders its whole set up front - Bestsellers and
   * Recently viewed from Liquid, YMAL from Nosto or the Shopify fallback - so paging is
   * reveal-in-place rather than a fetch: there is no next page to request. That also
   * keeps it uniform across the three tabs, including the one whose markup is Nosto's.
   */
  #setupPaging() {
    this.addEventListener('click', this.#handleLoadMoreClick);

    // Panels are filled asynchronously by three different owners (Nosto injection,
    // product-recommendations.js, this component's recently-viewed fetch); each announces
    // itself so paging can re-measure without polling the DOM.
    this.addEventListener('product-recommendations:updated', this.#requestSync);
    document.addEventListener('product-recommendations-tabs:content', this.#requestSync);

    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.#mobileQuery.addEventListener('change', this.#handleViewportChange);

    this.#syncPaging();
  }

  #handleViewportChange = () => this.#requestSync();

  #requestSync = () => {
    if (this.#syncFrame != null) return;
    this.#syncFrame = requestAnimationFrame(() => {
      this.#syncFrame = null;
      this.#syncPaging();
    });
  };

  /**
   * @param {Event} event
   */
  #handleLoadMoreClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-recs-load-more-button]');
    if (!button) return;

    const panel = button.closest('[role="tabpanel"]');
    if (!(panel instanceof HTMLElement)) return;

    const list = this.#activeList(panel);
    if (!list) return;

    const shown = Number(list.container.dataset.recsVisible) || PAGE_SIZE;
    list.container.dataset.recsVisible = String(shown + PAGE_SIZE);
    this.#syncPanel(panel);
  };

  /**
   * Paging is mobile-only - desktop keeps its horizontal slider - but that gate is CSS's
   * job, not this method's: base.css only acts on `data-recs-hidden` below 750px and hides
   * the button above it. Keeping the attributes correct at every width means a resize needs
   * no repair pass, which matters because a desktop pass that stripped them would leave the
   * grid unpaged if the breakpoint change were ever missed.
   *
   * What does disable paging is the mobile carousel opt-in (`carousel_on_mobile`): that
   * row scrolls horizontally, so every item has to stay reachable.
   * @returns {boolean}
   */
  #pagingDisabled() {
    return (
      this.classList.contains('product-recommendations-tabs--carousel-mobile') &&
      Boolean(this.#mobileQuery?.matches)
    );
  }

  #syncPaging() {
    this.refs.panels?.forEach((panel) => this.#syncPanel(panel));
  }

  /**
   * The list a panel is currently showing. A panel with a Nosto placement (YMAL, and
   * Bestsellers when configured) holds three - Nosto's picks as our own cards, Nosto's own
   * template, and the tab's fallback - and `pdp-nosto-carousel.js` marks which one is live.
   * Otherwise it is whichever of the cards grid and the fallback is showing (`#shownGrid`).
   * @param {HTMLElement} panel
   * @returns {{ container: HTMLElement, items: HTMLElement[] } | null}
   */
  #activeList(panel) {
    const nosto = panel.querySelector('[data-recs-nosto]');
    if (nosto instanceof HTMLElement && panel.hasAttribute('data-nosto-native')) {
      const container = nosto.querySelector('.swiper-wrapper, .nosto-list');
      const host = container instanceof HTMLElement ? container : nosto;
      const items = /** @type {HTMLElement[]} */ ([
        ...host.querySelectorAll(':scope > .swiper-slide, :scope > .nosto-product, :scope > .product-tile, :scope > .nosto-list-item'),
      ]);
      // Never fall through to the Shopify fallback while Nosto is filled - base.css hides
      // it in that state, so paging it would show a count for a list nobody can see.
      return items.length ? { container: host, items } : null;
    }

    const grid = this.#shownGrid(panel);
    if (!grid) return null;

    const items = /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > .resource-list__item')]);
    return items.length ? { container: grid, items } : null;
  }

  /**
   * @param {HTMLElement} panel
   */
  #syncPanel(panel) {
    const control = panel.querySelector('[data-recs-load-more]');
    const list = this.#activeList(panel);

    if (!list || this.#pagingDisabled()) {
      list?.items.forEach((item) => item.removeAttribute('data-recs-hidden'));
      if (control instanceof HTMLElement) control.hidden = true;
      return;
    }

    const { container, items } = list;
    const stored = Number(container.dataset.recsVisible);
    const visible = Math.min(Number.isFinite(stored) && stored > 0 ? stored : PAGE_SIZE, items.length);
    container.dataset.recsVisible = String(visible);

    items.forEach((item, index) => item.toggleAttribute('data-recs-hidden', index >= visible));

    if (!(control instanceof HTMLElement)) return;

    const remaining = items.length - visible;
    control.hidden = remaining <= 0;

    const count = control.querySelector('[data-recs-remaining]');
    if (count) count.textContent = String(remaining);
  }

  #prefetchRecommendations() {
    const { panels } = this.refs;
    const recsPanel = panels?.find((panel) => panel.dataset.tab === 'recommendations');
    recsPanel?.querySelector('product-recommendations')?.loadRecommendations();
    // Bestsellers can have a Nosto placement too, and it is the tab open on load.
    if (panels?.some((panel) => panel.dataset.nostoTabPlacement)) {
      document.dispatchEvent(new CustomEvent('product-recommendations-tabs:nosto', { bubbles: true }));
    }
  }

  #setupTabs() {
    const { tablist, tabs, panels } = this.refs;

    tablist?.addEventListener(
      'mouseenter',
      (event) => {
        const tab = /** @type {HTMLElement | null} */ (event.target)?.closest('.product-recommendations-tabs__tab');
        if (!tab || !(event instanceof MouseEvent)) return;

        this.#setUnderlineDirection(tab, event.clientX);
      },
      true
    );

    tabs?.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#activateTab(index, { focusTab: false }));
      tab.addEventListener('keydown', (event) => this.#handleTabKeydown(event, index));
    });

    panels?.forEach((panel, index) => {
      if (index === 0) return;
      panel.hidden = true;
    });
  }

  /**
   * @param {HTMLElement} tab
   * @param {number} clientX
   */
  #setUnderlineDirection(tab, clientX) {
    const rect = tab.getBoundingClientRect();
    const fromLeft = clientX < rect.left + rect.width / 2;
    tab.setAttribute('data-enter-from', fromLeft ? 'left' : 'right');
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handleTabKeydown(event, index) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    let nextIndex = index;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    this.#activateTab(nextIndex, { focusTab: true });
  }

  /**
   * @param {number} index
   * @param {{ focusTab?: boolean }} [options]
   */
  #activateTab(index, options = {}) {
    const { tabs, panels } = this.refs;
    if (!tabs?.length || !panels?.length) return;

    const previousIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

    tabs.forEach((tab, tabIndex) => {
      const isSelected = tabIndex === index;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;

      if (isSelected && previousIndex !== -1 && previousIndex !== tabIndex) {
        tab.setAttribute('data-enter-from', tabIndex > previousIndex ? 'left' : 'right');
      }
    });

    if (options.focusTab) {
      tabs[index]?.focus({ preventScroll: true });
    }

    panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
    });

    const activePanel = panels[index];
    if (activePanel?.dataset.tab === 'recommendations') {
      // Always refresh Shopify related products (also used as Nosto fallback).
      activePanel.querySelector('product-recommendations')?.loadRecommendations();
      if (activePanel.dataset.nostoTabPlacement) {
        document.dispatchEvent(new CustomEvent('product-recommendations-tabs:nosto', { bubbles: true }));
      }
    } else if (activePanel?.dataset.tab === 'recently-viewed') {
      this.#loadRecentlyViewed();
    }

    if (activePanel) this.#syncPanel(activePanel);
  }

  async #loadRecentlyViewed() {
    if (this.#recentlyViewedLoaded) return;

    const { panels } = this.refs;
    const recentlyViewedPanel = panels?.[2];
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;

    if (!recentlyViewedPanel || !sectionId) return;
    // Nosto's browsing history already filled the tab (pdp-nosto-carousel.js), so this
    // browser's own history - its fallback - is not needed.
    if (recentlyViewedPanel.hasAttribute('data-nosto-filled')) return;

    const viewedProducts = RecentlyViewed.getProducts().filter((id) => id !== productId);

    if (viewedProducts.length === 0) {
      this.#showRecentlyViewedEmpty();
      this.#recentlyViewedLoaded = true;
      return;
    }

    recentlyViewedPanel.dataset.loading = 'true';
    recentlyViewedPanel.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('type', 'product');

      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parsedDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const content = parsedDocument.querySelector(
        `#Panel-recently-viewed-${sectionId} [data-recently-viewed-content]`
      );
      const target = recentlyViewedPanel.querySelector('[data-recently-viewed-content]');

      if (content?.innerHTML.trim() && target) {
        target.innerHTML = content.innerHTML;
        const grid = recentlyViewedPanel.querySelector('[data-has-recommendations]');
        if (grid?.dataset.hasRecommendations === 'false') {
          this.#showRecentlyViewedEmpty();
        }
      } else {
        this.#showRecentlyViewedEmpty();
      }
    } catch (error) {
      console.error('Recently viewed products error:', error);
      this.#showRecentlyViewedEmpty();
    } finally {
      delete recentlyViewedPanel.dataset.loading;
      recentlyViewedPanel.removeAttribute('aria-busy');
      this.#recentlyViewedLoaded = true;
      this.#syncPanel(recentlyViewedPanel);
    }
  }

  #showRecentlyViewedEmpty() {
    const recentlyViewedPanel = this.refs.panels?.[2];
    if (!recentlyViewedPanel) return;

    recentlyViewedPanel.querySelector('[data-recently-viewed-content]')?.classList.add('hidden');
    const empty = recentlyViewedPanel.querySelector('.product-recommendations-tabs__empty');
    empty?.classList.remove('hidden');
    empty?.setAttribute('role', 'status');
  }
}

if (!customElements.get('product-recommendations-tabs')) {
  customElements.define('product-recommendations-tabs', ProductRecommendationsTabs);
}
